import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, ProgressBar, Tab, Tabs, ListGroup } from 'react-bootstrap';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import ContentViewer from '../components/ContentViewer';
import LearnerNavbar from '../components/LearnerNavbar';

const ModuleContentPage = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, isLearner, isAdmin, isFacilitator } = useAuth();
  const [module, setModule] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [contentFilter, setContentFilter] = useState('all');
  const [hasModuleAccess, setHasModuleAccess] = useState(false);
  const [accessCheckLoading, setAccessCheckLoading] = useState(true);

  const loadModule = async () => {
    try {
      setLoading(true);
      const moduleDoc = await getDoc(doc(db, 'modules', moduleId));
      
      if (moduleDoc.exists()) {
        const moduleData = moduleDoc.data();
        setModule({ id: moduleDoc.id, ...moduleData });
        
        // Auto-select first content item if available
        if (moduleData.content && moduleData.content.length > 0) {
          setSelectedContent(moduleData.content[0]);
        }
      } else {
        throw new Error('Module not found');
      }
    } catch (error) {
      devError('Error loading module:', error);
      alert(`Error loading module: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProgress(userData.contentProgress || {});
      }
    } catch (error) {
      devError('Error loading user progress:', error);
    }
  };

  const checkModuleAccess = useCallback(async () => {
    try {
      setAccessCheckLoading(true);
      
      // Admins and facilitators have access to all modules
      if (isAdmin() || isFacilitator()) {
        setHasModuleAccess(true);
        loadModule();
        loadUserProgress();
        return;
      }
      
      // For learners, check if they are enrolled in the module or assigned via cohort/program
      if (isLearner()) {
        const moduleDoc = await getDoc(doc(db, 'modules', moduleId));
        if (moduleDoc.exists()) {
          const moduleData = moduleDoc.data();
          const cohortIds = Array.isArray(moduleData.cohortIds)
            ? moduleData.cohortIds
            : (moduleData.cohortId ? [moduleData.cohortId] : []);

          const hasCohortConstraints = cohortIds.length > 0 || Boolean(moduleData.cohortId);
          const hasCohortAccess = userProfile?.cohortId
            ? cohortIds.includes(userProfile.cohortId)
            : false;
          const hasProgramAccess = userProfile?.programId
            ? moduleData.programId === userProfile.programId
            : false;

          if ((hasCohortConstraints && hasCohortAccess) || (!hasCohortConstraints && hasProgramAccess)) {
            setHasModuleAccess(true);
            loadModule();
            loadUserProgress();
            return;
          }
        }

        const enrollmentQuery = query(
          collection(db, 'enrollments'),
          where('userId', '==', currentUser.uid),
          where('moduleId', '==', moduleId)
        );
        const enrollmentSnapshot = await getDocs(enrollmentQuery);
        
        if (!enrollmentSnapshot.empty) {
          setHasModuleAccess(true);
          loadModule();
          loadUserProgress();
        } else {
          setHasModuleAccess(false);
        }
      } else {
        setHasModuleAccess(false);
      }
    } catch (error) {
      devError('Error checking module access:', error);
      setHasModuleAccess(false);
    } finally {
      setAccessCheckLoading(false);
    }
  }, [moduleId, currentUser, isAdmin, isFacilitator, isLearner, userProfile]);

  useEffect(() => {
    if (moduleId && currentUser && userProfile) {
      checkModuleAccess();
    }
  }, [moduleId, currentUser, userProfile, checkModuleAccess]);

  const handleContentSelect = (content) => {
    setSelectedContent(content);
  };

  const handleProgressUpdate = (progressData) => {
    setUserProgress(prev => ({
      ...prev,
      [progressData.contentId]: progressData
    }));
  };

  const getContentProgress = (contentId) => {
    return userProgress[contentId] || { percentage: 0, completed: false };
  };

  const getOverallProgress = () => {
    if (!module?.content || module.content.length === 0) return 0;
    
    const totalContent = module.content.length;
    const completedContent = module.content.filter(content => 
      getContentProgress(content.id).completed
    ).length;
    
    return Math.round((completedContent / totalContent) * 100);
  };

  const getFilteredContent = () => {
    if (!module?.content) return [];
    
    switch (contentFilter) {
      case 'completed':
        return module.content.filter(content => getContentProgress(content.id).completed);
      case 'in-progress':
        return module.content.filter(content => {
          const progress = getContentProgress(content.id);
          return progress.percentage > 0 && !progress.completed;
        });
      case 'not-started':
        return module.content.filter(content => getContentProgress(content.id).percentage === 0);
      case 'video':
        return module.content.filter(content => content.type === 'video');
      case 'document':
        return module.content.filter(content => content.type === 'document');
      default:
        return module.content;
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('video')) return '🎥';
    if (fileType?.includes('image')) return '🖼️';
    if (fileType?.includes('audio')) return '🎵';
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('document') || fileType?.includes('word')) return '📝';
    if (fileType?.includes('presentation') || fileType?.includes('powerpoint')) return '📊';
    return '📁';
  };

  // Authentication check
  if (!currentUser || !userProfile) {
    return <Navigate to="/login" replace />;
  }

  if (accessCheckLoading) {
    return (
      <>
        <LearnerNavbar />
        <Container className="mt-4">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Checking access permissions...</p>
            </div>
          </div>
        </Container>
      </>
    );
  }

  if (!hasModuleAccess) {
    return (
      <>
        <LearnerNavbar />
        <Container className="mt-4">
          <Alert variant="danger">
            <h6>Access Denied</h6>
            <p>You don't have permission to access this module content. Please contact your administrator if you believe this is an error.</p>
            <Button 
              variant="primary" 
              onClick={() => navigate(isLearner() ? '/dashboard' : isAdmin() ? '/admin' : '/facilitator')}
            >
              Return to Dashboard
            </Button>
          </Alert>
        </Container>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <LearnerNavbar />
        <Container className="mt-4">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading module content...</p>
            </div>
          </div>
        </Container>
      </>
    );
  }

  if (!module) {
    return (
      <>
        <LearnerNavbar />
        <Container className="mt-4">
          <Alert variant="warning">
            <h6>Module Not Found</h6>
            <p>The requested module could not be loaded.</p>
            <Button variant="primary" onClick={() => navigate(isLearner() ? '/dashboard' : isAdmin() ? '/admin' : '/facilitator')}>
              Return to Dashboard
            </Button>
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <LearnerNavbar />
      <Container fluid className="mt-4">
        {/* Module Header */}
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h2>{module.moduleName}</h2>
                    <p className="text-muted mb-2">{module.description}</p>
                    <div className="d-flex gap-2 align-items-center">
                      <Badge bg="primary">{module.difficulty}</Badge>
                      <Badge bg="info">⏱️ {module.estimatedHours}h</Badge>
                      <Badge bg="success">📚 {module.content?.length || 0} items</Badge>
                    </div>
                  </div>
                  <div className="text-end">
                    <h6>Your Progress</h6>
                    <ProgressBar 
                      now={getOverallProgress()} 
                      label={`${getOverallProgress()}%`}
                      style={{ minWidth: '200px' }}
                      variant={getOverallProgress() === 100 ? 'success' : 'primary'}
                    />
                    <small className="text-muted">
                      {module.content?.filter(c => getContentProgress(c.id).completed).length || 0} of {module.content?.length || 0} completed
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Content Sidebar */}
          <Col md={4} lg={3}>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">📚 Content</h6>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => navigate(isLearner() ? '/dashboard' : isAdmin() ? '/admin' : '/facilitator')}
                  >
                    ← Back
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {/* Content Filter Tabs */}
                <Tabs 
                  activeKey={contentFilter} 
                  onSelect={setContentFilter} 
                  className="small-tabs"
                  fill
                >
                  <Tab eventKey="all" title="All" />
                  <Tab eventKey="video" title="🎥" />
                  <Tab eventKey="document" title="📄" />
                </Tabs>
                
                <div className="px-2 py-2">
                  <div className="btn-group-vertical btn-group-sm w-100 mb-2" role="group">
                    <Button 
                      variant={contentFilter === 'not-started' ? 'primary' : 'outline-secondary'}
                      size="sm"
                      onClick={() => setContentFilter('not-started')}
                    >
                      📋 Not Started
                    </Button>
                    <Button 
                      variant={contentFilter === 'in-progress' ? 'warning' : 'outline-secondary'}
                      size="sm"
                      onClick={() => setContentFilter('in-progress')}
                    >
                      ⏳ In Progress
                    </Button>
                    <Button 
                      variant={contentFilter === 'completed' ? 'success' : 'outline-secondary'}
                      size="sm"
                      onClick={() => setContentFilter('completed')}
                    >
                      ✅ Completed
                    </Button>
                  </div>
                </div>

                <ListGroup variant="flush">
                  {getFilteredContent().length === 0 ? (
                    <ListGroup.Item className="text-center text-muted py-4">
                      <div>No content matches your filter</div>
                      <Button 
                        variant="link" 
                        size="sm"
                        onClick={() => setContentFilter('all')}
                      >
                        Show all content
                      </Button>
                    </ListGroup.Item>
                  ) : (
                    getFilteredContent().map((content, index) => {
                      const progress = getContentProgress(content.id);
                      const isSelected = selectedContent?.id === content.id;
                      
                      return (
                        <ListGroup.Item
                          key={content.id}
                          action
                          active={isSelected}
                          onClick={() => handleContentSelect(content)}
                          className={`content-item ${isSelected ? 'selected' : ''}`}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center mb-1">
                                <span className="me-2">
                                  {getFileIcon(content.file?.type)}
                                </span>
                                <h6 className="mb-0 small">{content.title}</h6>
                              </div>
                              <div className="d-flex gap-1 mb-2">
                                <Badge bg="light" text="dark" className="small">
                                  {content.type}
                                </Badge>
                                <Badge bg="light" text="dark" className="small">
                                  {content.category}
                                </Badge>
                              </div>
                              <div className="progress-container">
                                <ProgressBar
                                  now={progress.percentage}
                                  size="sm"
                                  variant={progress.completed ? 'success' : progress.percentage > 0 ? 'warning' : 'secondary'}
                                  style={{ height: '4px' }}
                                />
                                <small className="text-muted">
                                  {progress.completed ? '✅ Complete' : 
                                   progress.percentage > 0 ? `${progress.percentage}%` : 
                                   '📋 Not started'
                                  }
                                </small>
                              </div>
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>

          {/* Content Viewer */}
          <Col md={8} lg={9}>
            {selectedContent ? (
              <ContentViewer
                moduleId={moduleId}
                contentId={selectedContent.id}
                onProgressUpdate={handleProgressUpdate}
              />
            ) : (
              <Card>
                <Card.Body className="text-center py-5">
                  <div className="mb-3" style={{ fontSize: '3rem' }}>📚</div>
                  <h4>Welcome to {module.moduleName}</h4>
                  <p className="text-muted">
                    Select content from the sidebar to start learning.
                  </p>
                  {module.content && module.content.length > 0 && (
                    <Button
                      variant="primary"
                      onClick={() => handleContentSelect(module.content[0])}
                    >
                      Start with First Content
                    </Button>
                  )}
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .content-item {
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .content-item:hover {
          background-color: var(--light-blue, #E6F3FF);
        }
        
        .content-item.selected {
          background-color: var(--light-blue, #E6F3FF);
          border-left: 4px solid var(--primary-blue, #0066CC);
        }
        
        .progress-container {
          margin-top: 8px;
        }
        
        .small-tabs .nav-link {
          padding: 0.375rem 0.5rem;
          font-size: 0.875rem;
        }
      `}</style>
    </>
  );
};

export default ModuleContentPage;
